/**
 * Feature Flags Configuration
 *
 * Hardcoded feature flags for enabling/disabling new features during the strangler-fig migration.
 * These are committed to git for reproducible builds.
 *
 * To toggle a feature:
 * 1. Change the constant value in this file
 * 2. Commit the change
 * 3. Everyone gets the same build behavior
 */

import constants from '@playcastdotio/GlobalConfig';

/** Enable only after Phase 6 privileged/native/supervisor qualification; capability checks remain mandatory. */
export const USE_MAINTENANCE_SESSION_DISPATCH = constants.BACKGROUND_MAINTENANCE_ENABLED === true;
export const USE_MAINTENANCE_ACTIVITY_ADMISSION = constants.BACKGROUND_MAINTENANCE_ENABLED === true;

/**
 * Detect if we're in Prime mode (legacy Playcast)
 *
 * Prime mode detection (strangler-fig: old code still reads this):
 * - window.playcast exists (Electron environment)
 * - window.playcast.steamInstall === false (not a Pavilion install)
 */
const isPrimeMode = typeof window !== 'undefined' &&
                     window.playcast !== undefined &&
                     window.playcast.steamInstall === false;

/**
 * Detect if we're in Beacon mode (direct share-code experience)
 *
 * Beacon mode is explicitly set via the /beacon Electron argument.
 * Independent from isPrimeMode - they are separate flags.
 */
export const isBeaconMode = typeof window !== 'undefined' &&
                      window.playcast !== undefined &&
                      window.playcast.beaconMode === true;

/**
 * Pavilion mode: the default when neither Prime nor Beacon is active.
 */
const isPavilionMode = !isPrimeMode && !isBeaconMode;

/**
 * Enable the new MessageRouter pattern for coordinator-based architecture
 *
 * When `true`:
 * - Messages are routed through MessageRouter to coordinators
 * - Coordinators handle business logic and emit semantic events
 * - New architecture is active (dual operation with legacy)
 *
 * When `false` (default):
 * - Messages go directly to legacy state stores
 * - Old architecture remains active
 *
 * This is part of the strangler-fig pattern migration.
 *
 * @default true for Beacon/Pavilion, false for Prime
 */
export const USE_MESSAGE_ROUTER = !isPrimeMode;

/**
 * Enable the new PlayjectorCoordinator for game lifecycle management
 *
 * When `true`:
 * - PlayjectorCoordinator manages game launch/close and Steam login
 * - Uses PlayjectorService abstraction
 * - New coordinator-based architecture for Playjector
 *
 * When `false` (default):
 * - Legacy playjectorHostModeEvents.ts handles game lifecycle
 * - Direct IPC communication to Playjector
 *
 * This is part of the strangler-fig pattern migration.
 *
 * @default true for Beacon/Pavilion, false for Prime
 */
export const USE_PLAYJECTOR_COORDINATOR = !isPrimeMode;

/**
 * Enable the new SessionCoordinator for presence reporting
 *
 * When `true`:
 * - SessionCoordinator manages dynamic presence (status, playerCount, peer IDs, runningGame)
 * - Calculates static properties on init (guestAccount, gamingScore, isCloudHost, geolocation)
 * - Sends PRESENCE_UPDATE to realtime API
 * - New coordinator-based architecture for session/presence
 *
 * When `false` (default):
 * - Legacy matchmaker.ts sends PRESENCE_UPDATE with HOST_CONFIG
 * - Presence is tied to matchmaking state
 *
 * This is part of the strangler-fig pattern migration.
 *
 * @default true for Beacon/Pavilion, false for Prime
 */
export const USE_SESSION_COORDINATOR = !isPrimeMode;

/**
 * Enable the PavilionCoordinator for matchmaking-based lobby flows
 *
 * @default true for Pavilion mode, false otherwise
 */
export const USE_PAVILION_COORDINATOR = isPavilionMode;

/**
 * Enable the ProvisioningCoordinator for batch script provisioning
 *
 * When `true`:
 * - ProvisioningCoordinator receives scripts via WebSocket, forwards to Playjector, returns results
 * - Pavilion-only: provisioning is only relevant for managed fleet machines
 *
 * @default true for Pavilion mode, false otherwise
 */
export const USE_PROVISIONING_COORDINATOR = isPavilionMode;

/**
 * Enable the BeaconCoordinator for direct share-code experience
 *
 * When `true`:
 * - BeaconCoordinator manages share code lifecycle, player count gating
 * - Works with shared coordinators (Session, Media, Peer)
 * - No matchmaking, no invitations, no lobby/queue
 *
 * @default true for Beacon mode, false otherwise
 */
export const USE_BEACON_COORDINATOR = isBeaconMode;

/**
 * Enable the new MediaCoordinator for media capture and WebRTC track management
 *
 * When `true`:
 * - MediaCoordinator manages device selection, track capture, and stream lifecycle
 * - Works with PeerCoordinator to apply WebRTC optimizations
 *   (codec preferences, sender params, receiver low-latency settings)
 *
 * When `false`:
 * - Legacy media capture and track management remains active
 *
 * This is part of the strangler-fig pattern migration.
 *
 * @default true for Beacon/Pavilion, false for Prime
 */
export const USE_MEDIA_COORDINATOR = !isPrimeMode;

/**
 * Use legacy event listeners to send Playjector IPC messages
 *
 * When `true`:
 * - hostEvents.ts and invitationEvents.ts listeners send IPC to Playjector
 * - Legacy state store listeners trigger hostModeChanged IPC sends
 * - CONFLICTS with PavilionCoordinator's Playjector integration
 *
 * When `false`:
 * - Legacy listeners are disabled
 * - PavilionCoordinator is the sole authority for Playjector notifications
 * - Part of surgical rip-and-replace strategy
 *
 * ⚠️ IMPORTANT: Set to `false` when USE_MESSAGE_ROUTER is `true`
 * to avoid duplicate IPC sends and race conditions.
 *
 * @default true - Legacy system still active until coordinators are fully wired
 */
export const USE_LEGACY_PLAYJECTOR_EVENTS = !USE_MESSAGE_ROUTER;

/**
 * Use mock Playjector service instead of real IPC
 *
 * When `true`:
 * - MockPlayjectorService simulates game launch/Steam login
 * - No actual IPC calls to Playjector
 * - Useful when Playjector doesn't work (no guest mode, browser dev, etc.)
 *
 * When `false`:
 * - PlayjectorService uses real IPC to Playjector
 * - Actual game launching and Steam login
 *
 * NOTE: This is independent of USE_PLAYJECTOR_COORDINATOR.
 * You can mock even with the legacy system, or use real service with new coordinator.
 *
 * @default false - Use real Playjector service
 */
export let USE_MOCK_PLAYJECTOR = false;
if(constants.ENVIRONMENT !== 'development') {
  USE_MOCK_PLAYJECTOR = false;
}

/**
 * Log feature flag status on startup (development only)
 */
if (constants.ENVIRONMENT === 'development') {
  console.log('[Feature Flags]', {
    isPrimeMode,
    isBeaconMode,
    isPavilionMode,
    USE_MESSAGE_ROUTER,
    USE_SESSION_COORDINATOR,
    USE_PAVILION_COORDINATOR,
    USE_PROVISIONING_COORDINATOR,
    USE_BEACON_COORDINATOR,
    USE_PLAYJECTOR_COORDINATOR,
    USE_MEDIA_COORDINATOR,
    USE_MOCK_PLAYJECTOR,
    ENVIRONMENT: constants.ENVIRONMENT,
  });
}

/**
 * Log strangler-fig status on startup (all environments)
 */
if (isBeaconMode) {
  console.log('[Strangler-Fig] ✅ BEACON MODE - Direct share-code experience');
} else if (isPrimeMode) {
  console.log('[Strangler-Fig] 🚫 PRIME MODE (legacy) - Prime mode detected');
} else {
  console.log('[Strangler-Fig] ✅ PAVILION MODE - Matchmaking-based experience');
}
console.log(`[Strangler-Fig] Shared coordinators: ${isPrimeMode ? 'disabled (Prime legacy)' : 'enabled'}`);
